import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IdName } from '@core/models/interfaces/common';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { TreeNode } from 'primeng/api';
import { DropdownModule } from 'primeng/dropdown';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { forkJoin, Subject, takeUntil } from 'rxjs';

import { ReportTabOptions } from '@core/constants/report';
import { APP_PERMISSION } from '@core/enum/permisson';
import { ReportTabEnum } from '@core/enum/report';
import { Company } from '@core/models/interfaces/company';
import { LeadSource } from '@core/models/interfaces/lead';
import { PolicyStatusDetails } from '@core/models/interfaces/lead/lead-policy-status';
import { Product } from '@core/models/interfaces/product';
import { ProductType } from '@core/models/interfaces/product-type';
import { GetNoteParams, PerformanceParams, ProductDetailsReportParams } from '@core/models/interfaces/report';
import { UserDetail } from '@core/models/interfaces/user';
import { UserGroups } from '@core/models/interfaces/user-group';
import { CompanyService } from '@core/services/company.service';
import { LeadSourceService } from '@core/services/lead-source.service';
import { PolicyStatusService } from '@core/services/policies/policy-status.service';
import { ProductTypeService } from '@core/services/product-type.service';
import { ProductService } from '@core/services/product.service';
import { TeamService } from '@core/services/team.service';
import { UserGroupService } from '@core/services/user-groups.service';
import { LeadReportComponent } from './components/lead-report/lead-report.component';
import { PerformanceReportComponent } from './components/performance-report/performance-report.component';
import { PerformanceSelectionComponent } from './components/performance-selection/performance-selection.component';
import { ProductDetailsReportComponent } from './components/product-details-report/product-details-report.component';
import { ProductionDetailsSelectionComponent } from './components/production-details-selection/production-details-selection.component';
import { NoteReportComponent } from './components/note-report/note-report.component';
import { NoteSelectionComponent } from './components/note-selection/note-selection.component';
import { ActivatedRoute, Router } from '@angular/router';
import { parseArrayParam, parseDisplayNamesParam, findTreeNodesByKeys } from '@shared/utils/dataObject.util';

@Component({
  selector: 'app-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectButtonModule,
    DropdownModule,
    TranslateModule,
    MenuModule,
    MultiSelectModule,
    NgxPermissionsModule,
    ProductionDetailsSelectionComponent,
    PerformanceSelectionComponent,
    LeadReportComponent,
    ProductDetailsReportComponent,
    PerformanceReportComponent,
    NoteReportComponent,
    NoteSelectionComponent
  ],
  templateUrl: './report.component.html',
  styleUrl: './report.component.scss'
})
export class ReportComponent implements OnInit, OnDestroy {
  readonly Permission = APP_PERMISSION;
  readonly ReportTabEnum = ReportTabEnum;
  readonly translatePrefix = 'report.';
  readonly unsubscribe$ = new Subject();

  tabActive: ReportTabEnum = ReportTabEnum.PRODUCT;

  tabOptions = ReportTabOptions;
  productDetailsReportParams: ProductDetailsReportParams;
  performanceReportParams: PerformanceParams;
  leadReportParams: PerformanceParams;
  noteReportParams: GetNoteParams;

  isCanRunReportProductDetails = true;

  isRunningProductDetailsReport = false;
  isRunningPerformanceReport = false;
  isRunningLeadReport = false;
  isRunningNoteReport = false;

  commissionTeams: IdName[] = [];
  companies: Company[] = [];
  productTypes: ProductType[] = [];
  products: Product[] = [];
  sources: TreeNode[] = [];
  policyStatuses: TreeNode[] = [];
  userGroups: TreeNode[] = [];
  isLoading: boolean = false;

  constructor(
    private teamService: TeamService,
    private companyService: CompanyService,
    private productTypeService: ProductTypeService,
    private productService: ProductService,
    private sourceService: LeadSourceService,
    private policyStatusService: PolicyStatusService,
    private userGroupService: UserGroupService,
    private ngxPermissionsService: NgxPermissionsService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.subscribePermissions();

    this.isLoading = true;
    forkJoin([
      this.teamService.getTeamsByUserIds(),
      this.companyService.getCompanies({
        size: 100
      }),
      this.productTypeService.getProductTypes({
        size: 100
      }),
      this.productService.getProducts({
        size: 100
      }),
      //get AllUser
      this.sourceService.getSources({
        size: 100
      }),
      this.policyStatusService.getPagePolicyStatus({
        size: 100
      }),
      this.userGroupService.getGroupsPermission()
    ]).subscribe({
      next: results => {
        const [teamRes, companyRes, productTypeRes, productRes, sourcesRes, policyStatusRes, userGroupRes] = results;
        this.commissionTeams = teamRes.data.content;
        this.companies = companyRes.data.content;
        this.productTypes = productTypeRes.data.content;
        this.products = productRes.data.content;
        this.sources = sourcesRes.data.content.map(item => this.convertToTreeNode(item));
        this.policyStatuses = policyStatusRes.data.content.map(item => this.convertToTreeNode(item));
        this.userGroups = userGroupRes.data.content.map(item => this.convertUserGroupToTreeNode(item));

        this.isLoading = false;

        // Sau khi load xong toàn bộ master data (sources, statuses, userGroups, ...),
        // map queryParams vào từng loại params. Dùng takeUntil để khi back về từ page khác
        // (nếu component được reuse) vẫn lắng nghe và cập nhật params từ URL.
        this.route.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
          const type = (params['type'] as ReportTabEnum) || ReportTabEnum.PRODUCT;
          // Helper: tạo object giả { id, fullName } từ ids + names (hiển thị khi load URL, không gọi API).
          // fullName luôn trim để tránh khoảng trắng thừa trong dropdown (giống fix tab production).
          const buildMinimalUsers = (ids: string[], names: string[]): UserDetail[] =>
            ids.map((id, i) => ({ id, fullName: String(names[i] ?? id).trim() } as UserDetail));

          switch (type) {
            default:
            case ReportTabEnum.PRODUCT: {
              this.tabActive = ReportTabEnum.PRODUCT;

              // Chỉ map params và run report nếu có ít nhất 1 filter hoặc groupBy từ URL.
              // Tránh auto-run khi vào page lần đầu không có params.
              const hasParams = params['groupBy'] || params['createdFrom'] || params['createdTo'];
              if (!hasParams) break;

              const commissionTeamIds = parseArrayParam(params['commissionTeamIds']);
              const leadSourceIds = parseArrayParam(params['leadSources']);
              const statusIds = parseArrayParam(params['statuses']);
              const companyIds = parseArrayParam(params['companyIds']);
              const productTypeIds = parseArrayParam(params['productTypeIds']);
              const productIds = parseArrayParam(params['productIds']);
              const commissionOwnerIds = parseArrayParam(params['commissionOwnerIds']);
              const commissionAgentIds = parseArrayParam(params['commissionAgentIds']);
              const visibleFields = parseArrayParam(params['visibleFields']);
              const commissionOwnerNames = parseDisplayNamesParam(params['commissionOwnerNames']);
              const commissionAgentNames = parseDisplayNamesParam(params['commissionAgentNames']);
              
              const leadSources = leadSourceIds.length
                ? findTreeNodesByKeys(this.sources, leadSourceIds)
                : [];
              const statuses = statusIds.length
                ? findTreeNodesByKeys(this.policyStatuses, statusIds)
                : [];

              const commissionOwners = commissionOwnerIds.length
                ? buildMinimalUsers(commissionOwnerIds, commissionOwnerNames)
                : [];
              const commissionAgents = commissionAgentIds.length
                ? buildMinimalUsers(commissionAgentIds, commissionAgentNames)
                : [];

              this.productDetailsReportParams = {
                groupBy: params['groupBy'] || 'ALL',
                commissionTeamIds,
                leadSources,
                statuses,
                companyIds,
                productTypeIds,
                productIds,
                commissionOwnerIds,
                commissionAgentIds,
                commissionOwners,
                commissionAgents,
                effectiveFrom: params['effectiveFrom'],
                effectiveTo: params['effectiveTo'],
                createdFrom: params['createdFrom'],
                createdTo: params['createdTo'],
                visibleFields,
                type: ReportTabEnum.PRODUCT
              };
              break;
            }
            case ReportTabEnum.PERFORMANCE: {
              this.tabActive = ReportTabEnum.PERFORMANCE;

              // Chỉ run report nếu có params từ URL.
              const hasParams = params['createdFrom'] || params['createdTo'] || params['userIds'];
              if (!hasParams) break;

              const userIds = parseArrayParam(params['userIds']);
              const groupIds = parseArrayParam(params['groups']);
              const visibleFields = parseArrayParam(params['visibleFields']);
              const userNames = parseDisplayNamesParam(params['userNames']);

              const groups = groupIds.length
                ? findTreeNodesByKeys(this.userGroups, groupIds)
                : [];
              const users = userIds.length ? buildMinimalUsers(userIds, userNames) : [];

              this.performanceReportParams = {
                userIds,
                users,
                groups,
                createdFrom: params['createdFrom'],
                createdTo: params['createdTo'],
                visibleFields,
                type: ReportTabEnum.PERFORMANCE
              };
              break;
            }
            case ReportTabEnum.LEAD: {
              this.tabActive = ReportTabEnum.LEAD;

              // Chỉ run report nếu có params từ URL.
              const hasParams = params['createdFrom'] || params['createdTo'] || params['userIds'];
              if (!hasParams) break;

              const userIds = parseArrayParam(params['userIds']);
              const groupIds = parseArrayParam(params['groups']);
              const visibleFields = parseArrayParam(params['visibleFields']);
              const userNames = parseDisplayNamesParam(params['userNames']);

              const groups = groupIds.length
                ? findTreeNodesByKeys(this.userGroups, groupIds)
                : [];
              const users = userIds.length ? buildMinimalUsers(userIds, userNames) : [];

              this.leadReportParams = {
                userIds,
                users,
                groups,
                createdFrom: params['createdFrom'],
                createdTo: params['createdTo'],
                visibleFields,
                type: ReportTabEnum.LEAD
              };
              break;
            }
            case ReportTabEnum.NOTE: {
              this.tabActive = ReportTabEnum.NOTE;

              // Chỉ run report nếu có params từ URL.
              const hasParams = params['groupBy'] || params['createdFrom'] || params['createdTo'];
              if (!hasParams) break;

              const userIds = parseArrayParam(params['userIds']);
              const noteTypeIds = parseArrayParam(params['noteTypeIds']);
              const visibleFields = parseArrayParam(params['visibleFields']);
              const userNames = parseDisplayNamesParam(params['userNames']);

              const users = userIds.length ? buildMinimalUsers(userIds, userNames) : [];

              this.noteReportParams = {
                groupBy: params['groupBy'] || 'ALL',
                userIds,
                users,
                noteTypeIds,
                createdFrom: params['createdFrom'],
                createdTo: params['createdTo'],
                visibleFields,
                type: ReportTabEnum.NOTE
              };
              break;
            }
          }
        });
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  subscribePermissions() {
    const permissions = Object.keys(this.ngxPermissionsService.getPermissions());
    this.tabOptions = ReportTabOptions.filter(item => permissions.includes(item.permission));

    this.tabActive = this.tabOptions[0].value;
  }

  convertToTreeNode(item: LeadSource | PolicyStatusDetails): TreeNode {
    let treeNode: TreeNode = {
      key: item.id,
      label: item.name,
      data: {
        id: item.id,
        code: item.code
      },
      children: item.children && item.children.length > 0 ? item.children.map(this.convertToTreeNode) : [],
      leaf: !item.children || item.children.length === 0
    };

    return treeNode;
  }

  convertUserGroupToTreeNode(item: UserGroups): TreeNode {
    let treeNode: TreeNode = {
      key: item.id,
      label: item.name,
      data: {
        id: item.id
      },
      children: item.subGroups && item.subGroups.length > 0 ? item.subGroups.map(this.convertUserGroupToTreeNode) : [],
      leaf: !item.subGroups || item.subGroups.length === 0
    };

    return treeNode;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
